import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const navLinks = [
  { href: '/', label: 'Home' },
  { href: '/gallery', label: 'Gallery' },
  { href: '/amenities', label: 'Amenities' },
  { href: '/pricing', label: 'Pricing' },
  { href: '/faq', label: 'FAQ' },
  { href: '/booking', label: 'Book Now' },
];

export const Navbar = ({ transparent = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const bgClass = transparent ? 'bg-[#1B4D3E]/90 backdrop-blur-sm' : 'bg-[#F9F8F6] border-b border-[#E2E0DC]';
  const textClass = transparent ? 'text-white' : 'text-[#1C1C1C]';

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 ${bgClass} transition-colors duration-300`}>
      <div className="container-padding">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <span className={`font-serif text-2xl font-medium ${textClass}`}>
              Big Bear
            </span>
            <span className={`text-xs tracking-widest uppercase ${transparent ? 'text-white/70' : 'text-[#66605B]'}`}>
              Estate
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              link.label === 'Book Now' ? (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`text-sm tracking-wide px-4 py-2 rounded-md transition-colors duration-300 ${
                    transparent 
                      ? 'bg-white/90 text-[#1B4D3E] hover:bg-white font-medium' 
                      : 'bg-[#1B4D3E] text-white hover:bg-[#163d32] font-medium'
                  }`}
                >
                  {link.label}
                </Link>
              ) : (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`text-sm tracking-wide transition-colors duration-300 ${
                    location.pathname === link.href
                      ? transparent ? 'text-[#D4AF37]' : 'text-[#1B4D3E] font-semibold'
                      : transparent ? 'text-white/80 hover:text-white' : 'text-[#66605B] hover:text-[#1B4D3E]'
                  }`}
                >
                  {link.label}
                </Link>
              )
            ))}
          </div>

          {/* Contact CTA */}
          <div className="hidden lg:flex items-center gap-4">
            <a 
              href="tel:416-543-4749" 
              className={`flex items-center gap-2 text-sm ${transparent ? 'text-white' : 'text-[#1B4D3E]'}`}
            >
              <Phone className="w-4 h-4" />
              416.543.4749
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className={`lg:hidden p-2 ${textClass}`}
            data-testid="mobile-menu-toggle"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-[#F9F8F6] border-t border-[#E2E0DC]"
          >
            <div className="container-padding py-6 space-y-4">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setIsOpen(false)}
                  className={`block py-2 text-lg ${
                    location.pathname === link.href
                      ? 'text-[#1B4D3E] font-semibold'
                      : 'text-[#66605B]'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <div className="pt-4 border-t border-[#E2E0DC]">
                <a 
                  href="tel:416-543-4749" 
                  className="flex items-center gap-2 text-[#1B4D3E] font-medium"
                >
                  <Phone className="w-5 h-5" />
                  416.543.4749
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
